<script setup lang="ts">
import { inject, onBeforeUnmount, ref, watchEffect } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';
import vtkGlyph3DMapper from '@kitware/vtk.js/Rendering/Core/Glyph3DMapper';
import vtkSphereSource from '@kitware/vtk.js/Filters/Sources/SphereSource';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import { VtkViewContext } from '@/src/components/vtk/context';
import { onVTKEvent } from '@/src/composables/onVTKEvent';
import { useDentalCbctStore } from './cbctStore';
import { useDentalMeshStore } from './meshStore';
import { useDentalTaskStore } from './taskStore';
import { useDentalRegistrationStore } from './registrationStore';
import { useDentalSceneStore } from './sceneStore';
import { observeActor } from './actorObservation';
import { transformPoint, type Point3 } from './rigid';

const props = defineProps<{ space: 'patient' | 'mesh' }>();
const view = inject(VtkViewContext); if (!view) throw new Error('A vtk view is required.');
const cbct = useDentalCbctStore(); const mesh = useDentalMeshStore(); const task = useDentalTaskStore();
const reg = useDentalRegistrationStore(); const scene = useDentalSceneStore();
type Marker = { name: string; point: Point3; color: string };
const markers = ref<Marker[]>([]); const overlay = ref<HTMLElement>();
const labels = ref<{ name: string; color: string; x: number; y: number }[]>([]);
function glyph(kind: string, color: Point3) {
  const poly = vtkPolyData.newInstance(); const sphere = vtkSphereSource.newInstance({ thetaResolution: 12, phiResolution: 12 });
  const mapper = vtkGlyph3DMapper.newInstance({ orient: false, scaling: false, scalarVisibility: false });
  mapper.setInputData(poly); mapper.setInputConnection(sphere.getOutputPort(), 1);
  const actor = vtkActor.newInstance(); actor.setMapper(mapper); actor.setPickable(false); actor.setUseBounds(false);
  actor.getProperty().setColor(...color); actor.getProperty().setAmbient(0.5);
  view!.renderer.addActor(actor); const remove = observeActor(actor, view!.renderer, { kind, space: props.space });
  return { poly, sphere, actor, mapper, remove };
}
const ordinary = glyph(props.space === 'patient' ? 'cbct-landmarks' : 'ios-landmarks', [0.45, 0.78, 1]);
// When CT is hidden, standalone landmarks still provide camera/clipping bounds.
ordinary.actor.setUseBounds(true);
const source = glyph('registration-source', [0.27, 0.86, 0.8]);
const target = glyph('registration-target', [1, 0.75, 0.38]);
const residual = vtkPolyData.newInstance(); const lineMapper = vtkMapper.newInstance({ scalarVisibility: false }); lineMapper.setInputData(residual);
const lineActor = vtkActor.newInstance(); lineActor.setMapper(lineMapper); lineActor.setPickable(false); lineActor.setUseBounds(false);
lineActor.getProperty().setColor(1, 0.42, 0.42); lineActor.getProperty().setLineWidth(2);
view.renderer.addActor(lineActor); const removeLine = observeActor(lineActor, view.renderer, { kind: 'registration-residuals', space: props.space });
let frame = 0;
function scheduleLabels() {
  cancelAnimationFrame(frame);
  frame = requestAnimationFrame(() => {
    const element = overlay.value; const canvas = element?.parentElement?.querySelector('canvas');
    if (!element || !canvas || !scene.landmarkLabels || !scene.landmarksVisible) { labels.value = []; return; }
    const width = element.clientWidth; const height = element.clientHeight;
    const occupied: { x: number; y: number; width: number }[] = [];
    labels.value = markers.value.flatMap(marker => {
      const [x, y, depth] = view!.renderWindowView.worldToDisplay(...marker.point, view!.renderer);
      const px = x * width / canvas.width; const py = height - y * height / canvas.height;
      if (depth < 0 || depth > 1 || px < 0 || px >= width || py < 0 || py >= height) return [];
      const labelWidth = Math.min(180, marker.name.length * 6 + 10);
      let ly = py;
      while (occupied.some(box => Math.abs(box.y - ly) < 18 && px < box.x + box.width && px + labelWidth > box.x)) ly += 18;
      occupied.push({ x: px, y: ly, width: labelWidth });
      return [{ name: marker.name, color: marker.color, x: px, y: ly }];
    });
  });
}
onVTKEvent(view.renderer.getActiveCamera(), 'onModified', scheduleLabels);
useResizeObserver(overlay, scheduleLabels);
watchEffect(() => {
  const isRegistration = task.task?.kind === 'registration' || mesh.renderInVolume;
  const points = props.space === 'patient' ? cbct.landmarks.map(point => ({ name: point.name, point: [...point.position] as Point3, color: '#73c7ff' }))
    : mesh.landmarks.map(point => ({ name: point.name, point: transformPoint(mesh.matrix, point.xyz), color: '#73c7ff' }));
  ordinary.poly.getPoints().setData(new Float32Array(points.flatMap(point => point.point)), 3); ordinary.poly.modified();
  ordinary.actor.setVisibility(scene.landmarksVisible && !isRegistration && points.length > 0);
  const sources = isRegistration ? reg.pairs.map(pair => transformPoint(mesh.matrix, pair.source)) : [];
  const targets = isRegistration ? reg.pairs.map(pair => [...pair.target] as Point3) : [];
  for (const [layer, centres] of [[source, sources], [target, targets]] as const) {
    layer.poly.getPoints().setData(new Float32Array(centres.flat()), 3); layer.poly.modified();
    layer.actor.setVisibility(scene.landmarksVisible && centres.length > 0);
  }
  const radius = Number.isFinite(scene.landmarkRadius) ? Math.min(5, Math.max(0.1, scene.landmarkRadius)) : 0.65;
  for (const layer of [ordinary, source, target]) layer.sphere.setRadius(radius);
  residual.getPoints().setData(new Float32Array(sources.flatMap((point, index) => [...point, ...targets[index]])), 3);
  residual.getLines().setData(new Uint32Array(sources.flatMap((_, index) => [2, index * 2, index * 2 + 1]))); residual.modified();
  lineActor.setVisibility(isRegistration && scene.residualsVisible && scene.landmarksVisible && sources.length > 0);
  markers.value = isRegistration ? sources.flatMap((point, index) => [
    { name: `S${index + 1}`, point, color: '#45dccb' }, { name: `T${index + 1}`, point: targets[index], color: '#ffc060' },
  ]) : points;
  void scene.landmarkLabels; scheduleLabels(); view!.requestRender();
});
onBeforeUnmount(() => {
  cancelAnimationFrame(frame);
  for (const layer of [ordinary, source, target]) { layer.remove(); view!.renderer.removeActor(layer.actor); layer.actor.delete(); layer.mapper.delete(); layer.sphere.delete(); layer.poly.delete(); }
  removeLine(); view!.renderer.removeActor(lineActor); lineActor.delete(); lineMapper.delete(); residual.delete();
});
</script>
<template><div ref="overlay" class="dental-point-labels"><span v-for="(label,index) in labels" :key="index" :style="{ left: `${label.x + 8}px`, top: `${label.y - 8}px`, color: label.color }">{{ label.name }}</span></div></template>
<style scoped>
.dental-point-labels { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.dental-point-labels span { position: absolute; background: #07101dc9; border-radius: 3px; padding: 2px 5px; font-size: 10px; max-width: 180px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
</style>
